/** One grid square is one metre. Parcel sits axis-aligned; north is the A–D diagonal. */

export const TILE = 1;

export const PROPERTY_WIDTH = 31.5;
export const PROPERTY_LENGTH = 94.5;

export const HALF_W = PROPERTY_WIDTH / 2;
export const HALF_L = PROPERTY_LENGTH / 2;

/**
 * Parcel corners. A (old NW) is north, D (old SE) is south:
 * the A–D diagonal is the north–south axis.
 *
 *   A ------------- B
 *   |                 |
 *   |     31.5 m      | 94.5 m
 *   |                 |
 *   C ------------- D
 */
export const CORNERS = {
  A: { x: -HALF_W, z: HALF_L },
  B: { x: HALF_W, z: HALF_L },
  C: { x: -HALF_W, z: -HALF_L },
  D: { x: HALF_W, z: -HALF_L },
} as const;

export type CornerId = keyof typeof CORNERS;

const northLen = Math.hypot(CORNERS.A.x, CORNERS.A.z);
export const NORTH = {
  x: CORNERS.A.x / northLen,
  z: CORNERS.A.z / northLen,
};

/** Yaw that maps local +Z onto the A–D north direction. */
export const NORTH_YAW = Math.atan2(NORTH.x, NORTH.z);

export const GROUND_WIDTH = 80;
export const GROUND_LENGTH = 180;

export const COMPASS_RADIUS = 8;
export const COMPASS_GAP = 10;
export const COMPASS_X = NORTH.x * (northLen + COMPASS_GAP + COMPASS_RADIUS);
export const COMPASS_Z = NORTH.z * (northLen + COMPASS_GAP + COMPASS_RADIUS);

export const MERIDIAN_NORTH = {
  x: NORTH.x * (northLen + COMPASS_GAP + COMPASS_RADIUS * 2 + 5),
  z: NORTH.z * (northLen + COMPASS_GAP + COMPASS_RADIUS * 2 + 5),
};
export const MERIDIAN_SOUTH = {
  x: -NORTH.x * (northLen + 14),
  z: -NORTH.z * (northLen + 14),
};
export const SOUTH_WORD = {
  x: -NORTH.x * (northLen + 10),
  z: -NORTH.z * (northLen + 10),
};

/** Metres along D→B, then metres inward from that east boundary. */
export const PEAR_ALONG_DB = 17;
export const PEAR_INSET = 2;

export const PEAR_TREE = offsetFromEdge(CORNERS.D, CORNERS.B, PEAR_ALONG_DB, PEAR_INSET);

/** Walk a→b, then step toward the parcel interior. */
export function offsetFromEdge(
  a: { x: number; z: number },
  b: { x: number; z: number },
  alongM: number,
  inwardM: number,
): { x: number; z: number } {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  const ux = dx / len;
  const uz = dz / len;
  const midX = a.x + ux * alongM;
  const midZ = a.z + uz * alongM;
  let px = uz;
  let pz = -ux;
  if (px * -midX + pz * -midZ < 0) {
    px = -px;
    pz = -pz;
  }
  return {
    x: midX + px * inwardM,
    z: midZ + pz * inwardM,
  };
}
